import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Usuario, AuthContextType } from '../types';
import UsuariosServiceAPI from '../services/usuariosServiceAPI';
import SessoesService from '../services/sessoesService';
import LogsUsuariosService from '../services/logsUsuariosService';
import { PerfisService } from '../services/perfisService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('@sistema-up:token');
    localStorage.removeItem('@sistema-up:refreshToken');
    localStorage.removeItem('@sistema-up:usuario');
    localStorage.removeItem('lastActivity');
    setUser(null);
    setLoading(false);
  }, []);

  const login = async (login: string, senha: string, captcha?: string) => {
    try {
      setLoading(true);
      const { user: authUser, error, requiresCaptcha } = await UsuariosServiceAPI.login(login, senha, captcha);
      
      if (error) {
        const customError: any = new Error(error);
        customError.requiresCaptcha = requiresCaptcha;
        throw customError;
      }
      
      if (authUser) {
        PerfisService.limparCache();
        
        // Determinar perfil_id (UUID) e perfil (nome)
        const perfilRaw = (authUser.perfil as any) || 'admin';
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(perfilRaw);
        let perfilNome = perfilRaw;
        const perfilId = (authUser.perfil_id as any) || perfilRaw;
        
        // Se perfil é UUID (backend antigo sem join), buscar nome do perfil
        if (isUUID) {
          try {
            const { data: perfilData } = await PerfisService.buscarPorId(perfilRaw);
            if (perfilData?.nome) {
              perfilNome = perfilData.nome;
            }
          } catch (e) {
            console.error('Erro ao buscar nome do perfil:', e);
          }
        }
        
        const usuarioFormatado: Usuario = {
          id: authUser.id,
          email: authUser.email || '',
          nome: authUser.nome,
          perfil: perfilNome as any,
          filiais: authUser.filiais || [],
          ativo: authUser.ativo ?? true,
          created_at: authUser.created_at || new Date().toISOString(),
          updated_at: authUser.updated_at || new Date().toISOString(),
          primeiro_acesso: authUser.primeiro_acesso || false,
          foto_perfil: authUser.foto_perfil,
          perfil_id: perfilId
        };
        setUser(usuarioFormatado);
        // Salvar no localStorage
        localStorage.setItem('user', JSON.stringify(usuarioFormatado));
        
        // Registrar sessão ativa
        SessoesService.registrarSessao(usuarioFormatado).catch(e => console.error('Erro ao registrar sessão:', e));

        // Registrar log de login
        LogsUsuariosService.registrarLogin(usuarioFormatado).catch(e => console.error('Erro ao registrar log de login:', e));
        
        return usuarioFormatado;
      }
      
      return null;
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: { nome: string; email: string; senha: string; confirmarSenha: string }) => {
    try {
      setLoading(true);
      
      if (data.senha !== data.confirmarSenha) {
        throw new Error('As senhas não coincidem');
      }

      // Verificar se email já existe
      const { existe, error: checkError } = await UsuariosServiceAPI.verificarEmailExistente(data.email);
      if (checkError) {
        throw checkError;
      }
      if (existe) {
        throw new Error('Este email já está cadastrado no sistema');
      }

      const authData = await UsuariosServiceAPI.criar({
        login: data.email,
        nome: data.nome,
        email: data.email,
        senha: data.senha,
        perfil_id: 'fornecedor',
        primeiro_acesso: true,
        filiais_permitidas: ['FILIAL_1'],
        ativo: true
      });
      
      if (!authData) {
        throw new Error('Erro ao criar usuário');
      }
      
      // Fazer login automático após cadastro
      await login(data.email, data.senha);
    } catch (error: any) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      
      // Remover sessão ativa do usuário
      if (user) {
        await SessoesService.removerSessao(user.id);
        
        // Registrar log de logout
        await LogsUsuariosService.registrarLogout(user);
      }
      
      setUser(null);
      localStorage.removeItem('user');
      PerfisService.limparCache();
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<{ nome: string; filiais: string[] }>) => {
    try {
      if (!user) {
        throw new Error('Usuário não encontrado');
      }

      // Simulação de atualização - implementar com Supabase real
      setUser(prev => prev ? { ...prev, ...updates } : null);
    } catch (error: any) {
      throw error;
    }
  };

  const temPermissao = (permissao: string): boolean => {
    if (!user) return false;
    
    // Mapeamento de permissões por perfil
    const permissoesPorPerfil: Record<string, string[]> = {
      'admin': ['solicitarAgendamento', 'fecharDia', 'gerenciarUsuarios', 'gerenciarFiliais', 'verRelatorios', 'gerenciarCargas', 'editarCarga', 'excluirCarga', 'alterarStatus', 'gerenciarOcorrencias', 'validarDocumentos'],
      'gerente': ['solicitarAgendamento', 'fecharDia', 'gerenciarCargas', 'editarCarga', 'alterarStatus', 'gerenciarOcorrencias', 'validarDocumentos'],
      'operador': ['gerenciarCargas', 'editarCarga', 'alterarStatus', 'gerenciarOcorrencias'],
      'fornecedor': ['solicitarAgendamento', 'validarDocumentos']
    };
    
    const perfil = user.perfil;
    const permissoes = permissoesPorPerfil[perfil] || [];
    
    return permissoes.includes(permissao);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    temPermissao
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
